import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAgent } from "../../Context/AgentContext";
import { useAuth } from "../../Context/UnifiedAuthContext";

const formatImageUrl = (url) => {
  if (!url) return null;
  
  // Don't format data URLs or blob URLs (for previews)
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  
  // If URL already has protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // If URL starts with domain, add https://
  if (url.startsWith('app.akiya2.com')) {
    return `https://${url}`;
  }
  
  // If it's a relative path, construct full URL
  return `https://app.akiya2.com/${url}`;
};

const ProfileManagementPage = () => {
  const navigate = useNavigate();
  const [userImage, setUserImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const { 
    agent, 
    loading: contextLoading, 
    error: contextError, 
    updateProfile, 
    clearError 
  } = useAgent();
  
  const { updateUserImage } = useAuth();

  const [profileData, setProfileData] = useState({
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    facebook_link: '',
    twitter_link: '',
    linkedin_link: '',
    about_them: '',
    image: null,
    isAgent: false,
    agent_id: '',
    agent_type: 'individual',
    company: '',
    phone: '',
    business_address: '',
    bio: '',
    image_url: null,
    license_number: '',
    years_experience: '',
    specializations: [],
    service_areas: [],
    certifications: [],
    languages_spoken: [],
    office_hours: '',
    professional_summary: '',
    website: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Initialize profile data from AgentContext
  useEffect(() => {
    if (agent) {
      console.log('Initializing profile data from agent context:', agent);
      setProfileData({
        name: agent.name || '',
        first_name: agent.first_name || '',
        last_name: agent.last_name || '',
        email: agent.email || '',
        facebook_link: agent.facebook_link || '',
        twitter_link: agent.twitter_link || '',
        linkedin_link: agent.linkedin_link || '',
        about_them: agent.about_them || '',
        image: agent.image || null,
        isAgent: agent.isAgent || false,
        agent_id: agent.agent_id || '',
        agent_type: agent.agent_type || 'individual',
        company: agent.company || '',
        phone: agent.phone || '',
        business_address: agent.business_address || '',
        bio: agent.bio || '',
        image_url: agent.image_url,
        license_number: agent.license_number || '',
        years_experience: agent.years_experience?.toString() || '',
        specializations: agent.specializations || [],
        service_areas: agent.service_areas || [],
        certifications: agent.certifications || [],
        languages_spoken: agent.languages_spoken || [],
        office_hours: agent.office_hours || '',
        professional_summary: agent.professional_summary || '',
        website: agent.website || ''
      });
      
      // Set initial image preview from agent data
      if (agent.image_url) {
        setImagePreview(formatImageUrl(agent.image_url));
      }
    }
  }, [agent]);

  // Handle context errors
  useEffect(() => {
    if (contextError) {
      setError(contextError);
    }
  }, [contextError]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUserImage(file);
      
      // Create preview URL for immediate display
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddSpecialization = (specialization) => {
    if (specialization.trim() && !profileData.specializations.includes(specialization.trim())) {
      setProfileData(prev => ({
        ...prev,
        specializations: [...prev.specializations, specialization.trim()]
      }));
    }
  };

  const handleRemoveSpecialization = (index) => {
    setProfileData(prev => ({
      ...prev,
      specializations: prev.specializations.filter((_, i) => i !== index)
    }));
  };

  const handleAddServiceArea = (area) => {
    if (area.trim() && !profileData.service_areas.includes(area.trim())) {
      setProfileData(prev => ({
        ...prev,
        service_areas: [...prev.service_areas, area.trim()]
      }));
    }
  };

  const handleRemoveServiceArea = (index) => {
    setProfileData(prev => ({
      ...prev,
      service_areas: prev.service_areas.filter((_, i) => i !== index)
    }));
  };

  const handleAddCertification = (certification) => {
    if (certification.trim() && !profileData.certifications.includes(certification.trim())) {
      setProfileData(prev => ({
        ...prev,
        certifications: [...prev.certifications, certification.trim()]
      }));
    }
  };

  const handleRemoveCertification = (index) => {
    setProfileData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const handleAddLanguage = (language) => {
    if (language.trim() && !profileData.languages_spoken.includes(language.trim())) {
      setProfileData(prev => ({
        ...prev,
        languages_spoken: [...prev.languages_spoken, language.trim()]
      }));
    }
  };

  const handleRemoveLanguage = (index) => {
    setProfileData(prev => ({
      ...prev,
      languages_spoken: prev.languages_spoken.filter((_, i) => i !== index)
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    clearError();

    try {
      // Upload image first if there's a new image
      if (userImage) {
        console.log('Uploading new image:', userImage);
        await updateUserImage(userImage);
      }

      // Prepare update data
      const updateData = {
        ...profileData,
        years_experience: profileData.years_experience ? parseInt(profileData.years_experience) : null
      };

      console.log('Updating profile with data:', updateData);
      
      // Use the updateProfile method from AgentContext if available
      if (updateProfile) {
        await updateProfile(updateData);
      } else {
        // Fallback to direct API call
        const response = await fetch('https://app.akiya2.com/agents/update-agent-profile', {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(updateData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to update profile');
        }
      }
      
      setSuccess('Profile updated successfully!');
      
      // Clear success message and refresh after 2 seconds
      setTimeout(() => {
        setSuccess(null);
        window.location.reload(); // Refresh the page to show updates
      }, 100000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
    clearError();
  };

  if (contextLoading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-gradient-to-br from-[#10c9c0] to-[#0eb1a9] rounded-2xl flex items-center justify-center animate-pulse">
            <i className="fas fa-user text-white text-2xl"></i>
          </div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center">
            <i className="fas fa-exclamation-triangle text-white text-2xl"></i>
          </div>
          <p className="text-gray-600">Profile data not available</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" style={{ fontFamily: '"Libre Franklin", sans-serif' }}>
      {/* FontAwesome CDN */}
      <link 
        rel="stylesheet" 
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" 
        integrity="sha512-iecdLmaskl7CVkqkXNQ/ZH/XLlvWZOJyj7Yy7tcenmpD1ypASozpmT/E0iPtmFIB46ZmdtAc9eNBvH0H/ZpiBw==" 
        crossOrigin="anonymous" 
        referrerPolicy="no-referrer" 
      />

      {/* Header */}
      <header className="bg-[--medium-sea-green] text-white shadow-sm border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/agent/dashboard')}
                className="w-10 h-10 bg-gray-100 text-[--medium-sea-green] hover:text-[--sea-green] rounded-xl flex items-center justify-center transition-colors "
              >
                <i className="fas fa-arrow-left text-[--medium-sea-green] hover:text-[--sea-green]"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">Profile Management</h1>
                <p className="text-white">Manage your personal and professional information</p>
              </div>
            </div>
            
            <div className="text-right">
              <p className="font-medium text-white">{agent.name}</p>
              <p className="text-sm text-white">{agent.email}</p>
              {profileData.isAgent && (
                <p className="text-sm text-white">
                  <i className="fas fa-briefcase mr-1"></i>
                  Agent Account
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Message Display */}
      {(error || success) && (
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <div className={`border rounded-xl px-4 py-3 ${
            error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
          }`}>
            <div className="flex items-center">
              <i className={`fas ${error ? 'fa-exclamation-triangle' : 'fa-check-circle'} mr-2`}></i>
              <span>{error || success}</span>
              <button 
                onClick={clearMessages}
                className="ml-auto hover:opacity-70 transition-opacity"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Profile Header */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
              {/* Profile Image */}
              <div className="relative">
                <div className="w-32 h-32 bg-gradient-to-br from-[#10c9c0] to-[#0eb1a9] rounded-full flex items-center justify-center overflow-hidden">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <i className="fas fa-user text-white text-4xl"></i>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 bg-[#10c9c0] text-white rounded-full w-10 h-10 flex items-center justify-center cursor-pointer hover:bg-[#0eb1a9] transition-colors">
                  <i className="fas fa-camera"></i>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
              </div>

              {/* Basic Info */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{agent.name}</h2>
                <p className="text-gray-600 mb-1">{agent.email}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <div className="inline-flex items-center px-3 py-1 bg-[#10c9c0]/10 text-[--medium-sea-green] rounded-full text-sm font-medium">
                    <i className="fas fa-user mr-2"></i>
                    User Account
                    
                  </div>
                  {profileData.isAgent && (
                    <div className="inline-flex items-center px-3 py-1 bg-[#10c9c0]/10 text-[--medium-sea-green] rounded-full text-sm font-medium">
                      <i className="fas fa-briefcase mr-2"></i>
                      {profileData.agent_type === 'individual' ? 'Individual Agent' : 'Company Agent'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <form onSubmit={handleProfileSubmit} className="space-y-8">
            {/* Personal Information */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Personal Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium mb-3 text-gray-700">Display Name *</label>
                  <input 
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                    required
                  />
                </div>

                {/* <div>
                  <label className="block font-medium mb-3 text-gray-700">Email Address *</label>
                  <input 
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                    required
                  />
                </div> */}

                <div>
                  <label className="block font-medium mb-3 text-gray-700">First Name</label>
                  <input 
                    type="text"
                    value={profileData.first_name}
                    onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                  />
                </div>

                <div>
                  <label className="block font-medium mb-3 text-gray-700">Last Name</label>
                  <input 
                    type="text"
                    value={profileData.last_name}
                    onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                  />
                </div>

                <div>
                  <label className="block font-medium mb-3 text-gray-700">Phone Number</label>
                  <input 
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                    placeholder="+81 90-1234-5678"
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block font-medium mb-3 text-gray-700">About</label>
                <textarea 
                  value={profileData.about_them}
                  onChange={(e) => setProfileData({...profileData, about_them: e.target.value})}
                  placeholder="Tell us about yourself..."
                  rows="4"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all resize-none"
                />
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Social Links</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block font-medium mb-3 text-gray-700">
                    <i className="fab fa-facebook mr-2 text-blue-600"></i>
                    Facebook
                  </label>
                  <input 
                    type="url"
                    value={profileData.facebook_link}
                    onChange={(e) => setProfileData({...profileData, facebook_link: e.target.value})}
                    placeholder="https://facebook.com/username"
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                  />
                </div>

                <div>
                  <label className="block font-medium mb-3 text-gray-700">
                    <i className="fab fa-twitter mr-2 text-blue-400"></i>
                    Twitter
                  </label>
                  <input 
                    type="url"
                    value={profileData.twitter_link}
                    onChange={(e) => setProfileData({...profileData, twitter_link: e.target.value})}
                    placeholder="https://twitter.com/username"
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                  />
                </div>

                <div>
                  <label className="block font-medium mb-3 text-gray-700">
                    <i className="fab fa-linkedin mr-2 text-blue-700"></i>
                    LinkedIn
                  </label>
                  <input 
                    type="url"
                    value={profileData.linkedin_link}
                    onChange={(e) => setProfileData({...profileData, linkedin_link: e.target.value})}
                    placeholder="https://linkedin.com/in/username"
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Agent Information (only show if isAgent is true) */}
            {profileData.isAgent && (
              <>
                {/* Business Information */}
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Business Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium mb-3 text-gray-700">Agent Type</label>
                      <select 
                        value={profileData.agent_type}
                        onChange={(e) => setProfileData({...profileData, agent_type: e.target.value})}
                        className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                      >
                        <option value="individual">Individual Agent</option>
                        <option value="company">Company Agent</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-medium mb-3 text-gray-700">Agent ID</label>
                      <input 
                        type="text"
                        value={profileData.agent_id}
                        onChange={(e) => setProfileData({...profileData, agent_id: e.target.value})}
                        placeholder="AGENT-123456"
                        className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                      />
                    </div>

                    <div>
                      <label className="block font-medium mb-3 text-gray-700">Company Name</label>
                      <input 
                        type="text"
                        value={profileData.company}
                        onChange={(e) => setProfileData({...profileData, company: e.target.value})}
                        placeholder="Real Estate Co."
                        className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                      />
                    </div>

                    <div>
                      <label className="block font-medium mb-3 text-gray-700">License Number</label>
                      <input 
                        type="text"
                        value={profileData.license_number}
                        onChange={(e) => setProfileData({...profileData, license_number: e.target.value})}
                        placeholder="RE-123456"
                        className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                      />
                    </div>

                    <div>
                      <label className="block font-medium mb-3 text-gray-700">Years of Experience</label>
                      <input 
                        type="number"
                        value={profileData.years_experience}
                        onChange={(e) => setProfileData({...profileData, years_experience: e.target.value})}
                        placeholder="5"
                        min="0"
                        className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                      />
                    </div>

                    <div>
                      <label className="block font-medium mb-3 text-gray-700">Website</label>
                      <input 
                        type="url"
                        value={profileData.website}
                        onChange={(e) => setProfileData({...profileData, website: e.target.value})}
                        placeholder="https://yourwebsite.com"
                        className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                      />
                    </div>

                    <div>
                      <label className="block font-medium mb-3 text-gray-700">Office Hours</label>
                      <input 
                        type="text"
                        value={profileData.office_hours}
                        onChange={(e) => setProfileData({...profileData, office_hours: e.target.value})}
                        placeholder="Mon-Fri 9AM-6PM"
                        className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block font-medium mb-3 text-gray-700">Business Address</label>
                    <input 
                      type="text"
                      value={profileData.business_address}
                      onChange={(e) => setProfileData({...profileData, business_address: e.target.value})}
                      placeholder="Tokyo, Japan"
                      className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                    />
                  </div>

                  <div className="mt-6">
                    <label className="block font-medium mb-3 text-gray-700">Professional Summary</label>
                    <textarea 
                      value={profileData.professional_summary}
                      onChange={(e) => setProfileData({...profileData, professional_summary: e.target.value})}
                      placeholder="Brief professional summary..."
                      rows="3"
                      className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all resize-none"
                    />
                  </div>

                  <div className="mt-6">
                    <label className="block font-medium mb-3 text-gray-700">Bio/Description</label>
                    <textarea 
                      value={profileData.bio}
                      onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                      placeholder="Tell us about your expertise..."
                      rows="4"
                      className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all resize-none"
                    />
                  </div>
                </div>

                {/* Specializations */}
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Specializations</h3>
                  <TagInput
                    items={profileData.specializations}
                    onAdd={handleAddSpecialization}
                    onRemove={handleRemoveSpecialization}
                    placeholder="Add specialization (e.g., Residential, Commercial, Investment Properties)"
                  />
                </div>

                {/* Service Areas */}
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Service Areas</h3>
                  <TagInput
                    items={profileData.service_areas}
                    onAdd={handleAddServiceArea}
                    onRemove={handleRemoveServiceArea}
                    placeholder="Add service area (e.g., Tokyo, Shibuya, Shinjuku)"
                  />
                </div>

                {/* Certifications */}
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Certifications</h3>
                  <TagInput
                    items={profileData.certifications}
                    onAdd={handleAddCertification}
                    onRemove={handleRemoveCertification}
                    placeholder="Add certification (e.g., Real Estate License, Property Management)"
                  />
                </div>

                {/* Languages */}
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Languages Spoken</h3>
                  <TagInput
                    items={profileData.languages_spoken}
                    onAdd={handleAddLanguage}
                    onRemove={handleRemoveLanguage}
                    placeholder="Add language (e.g., Japanese, English, Mandarin)"
                  />
                </div>
              </>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <button 
                type="submit"
                disabled={loading}
                className={`px-8 py-4 rounded-xl font-medium transition-all duration-300 ${
                  loading
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-[--medium-sea-green] text-white hover:bg-[--sea-green] shadow-lg hover:shadow-xl '
                }`}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Updating Profile...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Save Profile
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

// Tag Input Component
const TagInput = ({ items, onAdd, onRemove, placeholder }) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      onAdd(inputValue);
      setInputValue('');
    }
  };

  const handleAddClick = () => {
    if (inputValue.trim()) {
      onAdd(inputValue);
      setInputValue('');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {items.map((item, index) => (
          <span
            key={index}
            className="inline-flex items-center px-3 py-1 bg-[#10c9c0]/10 text-[--medium-sea-green] rounded-full text-sm font-medium"
          >
            {item}
            <button
              onClick={() => onRemove(index)}
              className="ml-2 text-[--medium-sea-green] hover:text-gray-600 transition-colors"
            >
              <i className="fas fa-times text-xs"></i>
            </button>
          </span>
        ))}
      </div>
      
      <div className="flex space-x-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="flex-1 p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
        />
        <button
          type="button"
          onClick={handleAddClick}
          className="px-6 py-4 bg-[--medium-sea-green] text-white rounded-xl hover:bg-[--sea-green] transition-colors"
        >
          <i className="fas fa-plus"></i>
        </button>
      </div>
      
      <p className="text-sm text-gray-500 mt-2">
        Type and press Enter or click the + button to add items
      </p>
    </div>
  );
};

export default ProfileManagementPage;